import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CircleCheck, CpuIcon, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { PcmVersion } from "@/types/pcmVersion";
import { cn } from "@/lib/utils";
import { ChipPosSelector, WaferDie } from "@/components/chipPos/ChipPosSelector";
import { CreateMaskData, WaferVisualizationData, waferDiameterOptions } from "@/hooks/useMaskForm";

type MaskDialogContentProps = {
  // Dialog meta
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  submitLabel: string;
  onSubmit: () => void;
  isMutating: boolean;

  // PCM Version combobox behaviour differs between create and edit
  pcmVersionDisabled: boolean;

  // Optional chip-pos warning (edit only)
  chipPosCount?: number;

  // Form data & handlers from useMaskForm
  data: CreateMaskData;
  waferVisualizationData: WaferVisualizationData;
  selectedReferenceDie: string | null;
  pcmVersionsWithNewlyCreatedPcmVersion: PcmVersion[];
  setNewlyCreatedPcmVersion: (value: string) => void;
  handleChange: (field: keyof CreateMaskData, value: string) => void;
  handleBlur: (field: keyof CreateMaskData) => void;
  handleDieClick: (die: WaferDie) => void;
};

export const MaskDialogContent = ({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  onSubmit,
  isMutating,
  pcmVersionDisabled,
  chipPosCount = 0,
  data,
  waferVisualizationData,
  selectedReferenceDie,
  pcmVersionsWithNewlyCreatedPcmVersion,
  setNewlyCreatedPcmVersion,
  handleChange,
  handleBlur,
  handleDieClick,
}: MaskDialogContentProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-6 py-4">
        {/* Left Column: Form Fields */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="name" className="mb-2">
              Mask Name <span className="text-red-500">*</span>
            </Label>
            <Input
              disabled={isMutating}
              id="name"
              value={data.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Enter mask name"
            />
          </div>
          <div>
            <Label htmlFor="pcmVersion" className="mb-2">
              PCM Version <span className="text-red-500">*</span>
            </Label>
            <Combobox
              disabled={pcmVersionDisabled || isMutating}
              fullWidth
              modal
              options={pcmVersionsWithNewlyCreatedPcmVersion.map((m) => ({
                key: m.id,
                value: m.pcmVersion,
                label: m.pcmVersion,
              }))}
              placeholder="Select a PCM Version"
              searchPlaceholder="Type to search or create"
              onChange={(value) => {
                handleChange("pcmVersion", value);
                handleChange("oTestVersion", value);
              }}
              onCreate={setNewlyCreatedPcmVersion}
              value={data.pcmVersion}
            />
          </div>
          <div>
            <Label htmlFor="oTestVersion" className="mb-2">
              OTEST Version
            </Label>
            <Input
              disabled
              id="oTestVersion"
              value={data.oTestVersion}
              onChange={undefined}
              placeholder="-"
            />
          </div>
          <div>
            <RadioGroupPrimitive.Root
              defaultValue={data.waferDiameterInMm}
              className="w-full grid grid-cols-2 gap-4 pt-2"
              onValueChange={(value) => handleChange("waferDiameterInMm", value)}
            >
              {waferDiameterOptions.map((option) => (
                <RadioGroupPrimitive.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    "relative group ring-[1px] ring-border rounded py-2 px-3 text-start",
                    "data-[state=checked]:ring-2 data-[state=checked]:ring-blue-500",
                  )}
                >
                  <CircleCheck className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 h-6 w-6 text-primary fill-blue-500 stroke-white group-data-[state=unchecked]:hidden" />
                  <CpuIcon className="mb-2.5 text-muted-foreground" />
                  <span className="font-semibold tracking-tight">{option.label}</span>
                  <p className="text-xs">{option.description}</p>
                </RadioGroupPrimitive.Item>
              ))}
            </RadioGroupPrimitive.Root>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dieWidth" className="mb-2">
                Die Width (µm)
              </Label>
              <Input
                disabled={isMutating}
                id="dieWidth"
                type="number"
                step="1"
                min="5000"
                value={data.dieWidth}
                onChange={(e) => handleChange("dieWidth", e.target.value)}
                onBlur={() => handleBlur("dieWidth")}
                placeholder="Enter die width"
              />
            </div>
            <div>
              <Label htmlFor="dieHeight" className="mb-2">
                Die Height (µm)
              </Label>
              <Input
                disabled={isMutating}
                id="dieHeight"
                type="number"
                step="1"
                min="5000"
                value={data.dieHeight}
                onChange={(e) => handleChange("dieHeight", e.target.value)}
                onBlur={() => handleBlur("dieHeight")}
                placeholder="Enter die height"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 mb-2">
                <Label htmlFor="dieOffsetX">Die Offset X (µm)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-full p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Learn about die offset X"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72" align="start">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Die Offset</h4>
                      <p className="text-xs text-muted-foreground">
                        The die offset shifts the die grid relative to the wafer center. Use this
                        when the die array is not centered on the wafer origin.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">Offset X:</span> Horizontal shift of the die
                        grid in µm.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">Offset Y:</span> Vertical shift of the die
                        grid in µm.
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Input
                disabled={isMutating}
                id="dieOffsetX"
                type="number"
                step="1"
                value={data.dieOffsetX}
                onChange={(e) => handleChange("dieOffsetX", e.target.value)}
                onBlur={() => handleBlur("dieOffsetX")}
                placeholder="Enter X offset"
              />
            </div>
            <div>
              <Label htmlFor="dieOffsetY" className="mb-2">
                Die Offset Y (µm)
              </Label>
              <Input
                disabled={isMutating}
                id="dieOffsetY"
                type="number"
                step="1"
                value={data.dieOffsetY}
                onChange={(e) => handleChange("dieOffsetY", e.target.value)}
                onBlur={() => handleBlur("dieOffsetY")}
                placeholder="Enter Y offset"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="referenceDieX" className="mb-2">
                Reference Die X (µm)
              </Label>
              <Input
                disabled
                id="referenceDieX"
                type="number"
                step="1"
                value={data.referenceDieX}
                onChange={undefined}
                placeholder="Select die on wafer"
              />
            </div>
            <div>
              <Label htmlFor="referenceDieY" className="mb-2">
                Reference Die Y (µm)
              </Label>
              <Input
                disabled
                id="referenceDieY"
                type="number"
                step="1"
                value={data.referenceDieY}
                onChange={undefined}
                placeholder="Select die on wafer"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1 mb-2">
              <Label htmlFor="edgeIndent">Edge Indent (mm)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Learn about edge indent"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="start">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Edge Indent</h4>
                    <p className="text-xs text-muted-foreground">
                      The minimum distance (in mm) from the wafer edge within which dies are
                      considered unmeasurable. Dies whose footprint falls within this exclusion zone
                      are excluded from the layout.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              disabled={isMutating}
              id="edgeIndent"
              type="number"
              step="0.5"
              min="0"
              value={data.edgeIndent}
              onChange={(e) => handleChange("edgeIndent", e.target.value)}
              onBlur={() => handleBlur("edgeIndent")}
              placeholder="Enter edge indent"
            />
          </div>
        </div>

        {/* Right Column: Wafer Visualization */}
        <div className="flex flex-col">
          <Label className="mb-2">Wafer Visualization</Label>
          <div className="flex-1 border rounded-lg p-4 bg-gray-50">
            <ChipPosSelector
              diameter={Number(waferVisualizationData.waferDiameterInMm) || 200}
              dieWidth={Number(waferVisualizationData.dieWidth) || 22310}
              dieHeight={Number(waferVisualizationData.dieHeight) || 14870}
              dieOffsetX={
                waferVisualizationData.dieOffsetX ? Number(waferVisualizationData.dieOffsetX) : null
              }
              dieOffsetY={
                waferVisualizationData.dieOffsetY ? Number(waferVisualizationData.dieOffsetY) : null
              }
              referenceDieX={
                waferVisualizationData.referenceDieX
                  ? Number(waferVisualizationData.referenceDieX)
                  : null
              }
              referenceDieY={
                waferVisualizationData.referenceDieY
                  ? Number(waferVisualizationData.referenceDieY)
                  : null
              }
              edgeIndent={Number(waferVisualizationData.edgeIndent) || 5}
              onDieClick={handleDieClick}
              selectedDieIds={selectedReferenceDie ? [selectedReferenceDie] : []}
              className="w-full h-full"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Click on a die to set it as the reference die
          </p>
        </div>
      </div>
      {chipPosCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <p className="text-sm text-amber-700">
            ⚠️ Updating these values might impact {chipPosCount} chip position{" "}
            {chipPosCount === 1 ? "file" : "files"} associated with this mask.
          </p>
        </div>
      )}
      <DialogFooter>
        <Button
          disabled={isMutating}
          type="button"
          variant="secondary"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={data.name.length < 1 || !data.pcmVersion || isMutating}
        >
          {submitLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
